import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "https://farmersagrohub.com";
const DEFAULT_OG = `${SITE}/og-cover.jpg`;
const DEFAULT_TITLE = "FarmersAgroHub — India's #1 Agriculture Marketplace";
const DEFAULT_DESC = "Buy genuine seeds, pesticides, fertilizers, irrigation & farm machinery from GST-verified sellers. Pan-India delivery. COD available.";

/**
 * SeoHead — per-page SEO tags.
 *
 * Props:
 *  - title:        page title (will be appended with " | FarmersAgroHub" unless absolute=true)
 *  - description:  page description (150-160 chars ideal)
 *  - path:         current path, e.g. "/products" (auto-builds canonical)
 *  - image:        OG/Twitter image absolute URL (defaults to brand cover)
 *  - noindex:      true to add <meta name="robots" content="noindex">
 *  - absolute:     true → don't append brand suffix to title
 *  - jsonLd:       a single object OR array of JSON-LD structured data objects
 */
const SeoHead = ({
  title,
  description = DEFAULT_DESC,
  path = "",
  image = DEFAULT_OG,
  noindex = false,
  absolute = false,
  jsonLd,
}) => {
  const fullTitle = !title ? DEFAULT_TITLE : absolute ? title : `${title} | FarmersAgroHub`;
  const canonical = `${SITE}${path || ""}`;
  const ldList = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="FarmersAgroHub" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {ldList.map((ld, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
      ))}
    </Helmet>
  );
};

// ---- Reusable JSON-LD builders ----
export const breadcrumbJsonLd = (trail) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: trail.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: item.url.startsWith("http") ? item.url : `${SITE}${item.url}`,
  })),
});

export const productJsonLd = (product) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: product.name,
  description: product.description,
  image: (product.images || []).map((i) => (i.startsWith("http") ? i : `${SITE}${i}`)),
  brand: { "@type": "Brand", name: product.brand || "FarmersAgroHub" },
  sku: product.sku || product.id,
  category: product.category,
  aggregateRating: product.num_reviews
    ? {
        "@type": "AggregateRating",
        ratingValue: product.rating,
        reviewCount: product.num_reviews,
      }
    : undefined,
  offers: {
    "@type": "Offer",
    url: `${SITE}/product/${product.id}`,
    priceCurrency: "INR",
    price: product.price,
    availability: product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    seller: { "@type": "Organization", name: product.seller_name || "FarmersAgroHub" },
  },
});

export const faqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export default SeoHead;
